import type { RouteRecord } from 'vite-react-ssg'
import { Outlet, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from 'react-hot-toast'
import { AuthProvider } from './contexts/AuthContext'
import Layout from './components/Layout'

// Import blog index for getStaticPaths
import blogIndexData from '../public/blog-data/index.json'

// Page Imports
import HomePage from './pages/HomePage'
import WcagValidatorPage from './pages/WcagValidatorPage'
import AdaCompliancePage from './pages/AdaCompliancePage'
import ContrastCheckerPage from './pages/ContrastCheckerPage'
import ColorBlindnessSimulatorPage from './pages/ColorBlindnessSimulatorPage'
import AccessibilityStatementPage from './pages/AccessibilityStatementPage'
import ProjectsPage from './pages/ProjectsPage'
import AboutPage from './pages/AboutPage'
import AccessibilityGuidePage from './pages/AccessibilityGuidePage'
import GuideCasinoAccessibilityPage from './pages/GuideCasinoAccessibilityPage'
import GuideTradingAccessibilityPage from './pages/GuideTradingAccessibilityPage'
import LoginPage from './pages/LoginPage'
import ScanPage from './pages/ScanPage'
import ScanResultPage from './pages/ScanResultPage'
import { BlogListSSG } from './pages/BlogListSSG'
import { BlogPostSSG } from './pages/BlogPostSSG'
import BatchScanPage from './pages/BatchScanPage'
import ReportsPage from './pages/ReportsPage'
import ReportDetailPage from './pages/ReportDetailPage'
import DashboardPage from './pages/DashboardPage'
import SettingsPage from './pages/SettingsPage'
import ProtectedRoute from './components/ProtectedRoute'

// QueryClient instance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
    },
  },
})

// Root wrapper with all providers (Head from vite-react-ssg doesn't need a provider)
function AppWrapper() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Layout>
          <Outlet />
        </Layout>
        {typeof window !== 'undefined' && (
          <Toaster
            position="bottom-right"
            toastOptions={{
              duration: 4000,
              style: { background: '#333', color: '#fff' },
            }}
          />
        )}
      </AuthProvider>
    </QueryClientProvider>
  )
}

export const routes: RouteRecord[] = [
  {
    path: '/',
    element: <AppWrapper />,
    children: [
      // === PRE-RENDERED (SSG) ===
      { index: true, element: <HomePage /> },
      { path: 'wcag-validator', element: <WcagValidatorPage /> },
      { path: 'ada-compliance-checker', element: <AdaCompliancePage /> },
      { path: 'contrast-checker', element: <ContrastCheckerPage /> },
      { path: 'color-blindness-simulator', element: <ColorBlindnessSimulatorPage /> },
      { path: 'accessibility-statement-generator', element: <AccessibilityStatementPage /> },
      { path: 'projects', element: <ProjectsPage /> },
      { path: 'about', element: <AboutPage /> },
      { path: 'accessibility-guide', element: <AccessibilityGuidePage /> },
      // === HIDDEN INDEX-PROBE PAGES (prerendered, unlinked, not in main sitemap) ===
      { path: 'guides/accessible-online-casino-interfaces', element: <GuideCasinoAccessibilityPage /> },
      { path: 'guides/accessible-online-trading-platforms', element: <GuideTradingAccessibilityPage /> },
      { path: 'scan', element: <ScanPage /> },
      { path: 'scan/:id', element: <ScanResultPage /> },
      { path: 'ada-compliance', element: <Navigate to="/ada-compliance-checker" /> },

      // === BLOG (SSG) ===
      { path: 'blog', element: <BlogListSSG /> },
      {
        path: 'blog/:slug',
        element: <BlogPostSSG />,
        // Generate static paths for all blog posts
        getStaticPaths: () => (blogIndexData as any[]).map(post => `/blog/${post.slug}`)
      },
      { path: 'login', element: <LoginPage /> },
      {
        path: 'batch',
        element: <ProtectedRoute><BatchScanPage /></ProtectedRoute>
      },
      {
        path: 'reports',
        element: <ProtectedRoute><ReportsPage /></ProtectedRoute>
      },
      {
        path: 'reports/:reportId',
        element: <ProtectedRoute><ReportDetailPage /></ProtectedRoute>
      },
      {
        path: 'dashboard',
        element: <ProtectedRoute><DashboardPage /></ProtectedRoute>
      },
      {
        path: 'settings',
        element: <ProtectedRoute><SettingsPage /></ProtectedRoute>
      },
    ],
  },
]
